/**
 * The payment flows in flight, by deal — outside any component (app#323, review).
 *
 * A run the progress panel gave up on (the wallet prompt timed out) is still at its prompt in the
 * wallet, and may yet get its answer. Until it settles, a second run for the same deal would open
 * a second creation prompt — the one thing the pay dialogs must never do. The dialog that started
 * the run can be closed and reopened meanwhile, so the memory of it cannot live in the dialog's
 * instance: it lives here, keyed by the deal, and a flow removes itself when it settles.
 */

const flows = new Map<string, Promise<unknown>>();

/** Remembers a flow for its deal until it settles, however it settles. */
export const rememberFlow = (dealId: string, flow: Promise<unknown>): void => {
  flows.set(dealId, flow);

  const forget = () => {
    if (flows.get(dealId) === flow) {
      flows.delete(dealId);
    }
  };

  flow.then(forget, forget);
};

/** Whether a flow for this deal is still running — at a wallet prompt, or waiting on the network. */
export const flowInFlight = (dealId: string): boolean => flows.has(dealId);

/** For tests only: forget every flow. */
export const forgetAllFlows = (): void => {
  flows.clear();
};

export const FLOW_IN_FLIGHT_MESSAGE =
  'Your wallet still has a prompt open from the previous attempt, or the previous transaction is still being confirmed. Confirm or reject it in the wallet, then try again.';
